import { useEffect, useState } from "react";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Geometry from "@arcgis/core/geometry/Geometry";
import {LayersDataTypes} from "../types/layers-data-types";
import {useAppSelector} from "./useAppSelector";
import { LayerType } from "../types/layer-type";

export interface InfrastructureType {
    electricity: boolean;
    water: boolean;
    sewerage: boolean;
    gas: boolean;
    telecom: boolean;
}

export default function useInfrastructure(geometry: Geometry): InfrastructureType {
    const layers = useAppSelector(state => state.map.layers)
    const [infrastructure, setInfrastructure] = useState<InfrastructureType>({
        electricity: false,
        water: false,
        sewerage: false,
        gas: false,
        telecom: false
    });

    useEffect(() => {
        setInfrastructure({
            electricity: false,
            water: false,
            sewerage: false,
            gas: false,
            telecom: false
        });

        if (!geometry) return;

        const checkInfrastructure = async () => {
            const electricity = await hasInfrastructure(LayersDataTypes.ElectricityInfrastructure);
            const water = await hasInfrastructure(LayersDataTypes.WaterInfrastructure);
            const sewerage = await hasInfrastructure(LayersDataTypes.SewerageInfrastructure);
            const gas = await hasInfrastructure(LayersDataTypes.GasInfrastructure);
            const telecom = await hasInfrastructure(LayersDataTypes.TelecomInfrastructure);
            setInfrastructure({ electricity, water, sewerage, gas, telecom });
        };
        checkInfrastructure();
    }, [geometry]);

    async function hasInfrastructure(dataType: number): Promise<boolean> {
        const counts = await Promise.all(
            layers
                .filter(layer => dataType === layer.dataType && layer.type === LayerType.ArcgisFeatureServer)
                .map(async layer => {
                    const featureLayer = new FeatureLayer({
                        url: layer.url,
                        definitionExpression: layer.style2D?.query
                    });
                    const query = featureLayer.createQuery();
                    const count = await featureLayer.queryFeatureCount({
                        ...query,
                        geometry: geometry,
                        distance: 100,
                        units: "meters",
                        spatialRelationship: "intersects"
                    });
                    return count;
                })
        );
        return counts.some(count => count > 0);
    }

    return infrastructure;
}
